import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useRef, useEffect } from 'react';
export function getStatusColor(statusCategory) {
    switch (statusCategory) {
        case 'done': return 'bg-green-100 text-green-800';
        case 'indeterminate': return 'bg-blue-100 text-blue-800';
        case 'new': return 'bg-gray-100 text-gray-800';
        default: return 'bg-gray-100 text-gray-600';
    }
}
/** Return Mon-Fri dates for the week starting at weekStartDate (a Monday). */
export function getWeekDays(weekStart) {
    const d = new Date(weekStart + 'T00:00:00');
    const days = [];
    for (let i = 0; i < 5; i++) {
        const day = new Date(d);
        day.setDate(d.getDate() + i);
        days.push(day.toISOString().slice(0, 10));
    }
    return days;
}
export function formatDayHeader(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
    const dayDate = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return { dayName, dayDate };
}
export function formatWeekLabel(weekStart) {
    const d = new Date(weekStart + 'T00:00:00');
    return `Week of ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}
/** Determine which day column an issue belongs to. */
export function getIssueDayIndex(issue, weekDays) {
    // If startDate falls within Mon-Fri → use that day
    if (issue.startDate) {
        const idx = weekDays.indexOf(issue.startDate);
        if (idx >= 0)
            return idx;
    }
    // If dueDate falls within Mon-Fri → use that day
    if (issue.dueDate) {
        const idx = weekDays.indexOf(issue.dueDate);
        if (idx >= 0)
            return idx;
    }
    // Default to Monday (index 0)
    return 0;
}
export function WeekCalendarModal({ weekStartDate, projectKey, projectId, hours, issues: initialIssues, jiraInstanceUrl, onClose, onIssuesMoved, }) {
    const weekDays = getWeekDays(weekStartDate);
    // Local mutable issue list with day assignments
    const [issues, setIssues] = useState(() => initialIssues.map(iss => ({
        ...iss,
        dayIndex: getIssueDayIndex(iss, weekDays),
    })));
    const [dragOverDay, setDragOverDay] = useState(null);
    const [pendingChanges, setPendingChanges] = useState(0);
    const [movingIssue, setMovingIssue] = useState(null);
    const dragIssueRef = useRef(null);
    const modalRef = useRef(null);
    const hasMovedRef = useRef(false);
    // Close on Escape
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);
    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                onClose();
            }
        };
        // Delay to avoid the popover click triggering immediate close
        const timer = setTimeout(() => {
            window.addEventListener('mousedown', handler);
        }, 100);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('mousedown', handler);
        };
    }, [onClose]);
    const handleDragStart = useCallback((e, issueKey) => {
        dragIssueRef.current = issueKey;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', issueKey);
    }, []);
    const handleDragOver = useCallback((e, dayIndex) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverDay(dayIndex);
    }, []);
    const handleDragLeave = useCallback(() => {
        setDragOverDay(null);
    }, []);
    const handleDrop = useCallback(async (e, targetDayIndex) => {
        e.preventDefault();
        setDragOverDay(null);
        const issueKey = dragIssueRef.current;
        if (!issueKey)
            return;
        const issue = issues.find(i => i.issueKey === issueKey);
        if (!issue || issue.dayIndex === targetDayIndex)
            return;
        const targetDate = weekDays[targetDayIndex];
        const oldStartDate = issue.startDate;
        const oldDueDate = issue.dueDate;
        // Optimistic update
        setIssues(prev => prev.map(i => i.issueKey === issueKey
            ? { ...i, dayIndex: targetDayIndex, startDate: targetDate, dueDate: targetDate }
            : i));
        setMovingIssue(issueKey);
        hasMovedRef.current = true;
        try {
            const resp = await fetch('/api/resources/allocations/move-issue-day', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ issueKey, projectId, targetDate, oldStartDate, oldDueDate }),
            });
            if (!resp.ok)
                throw new Error('Failed to move issue');
            const data = await resp.json();
            setPendingChanges(prev => prev + (data.writeBackCount || 0));
        }
        catch {
            // Revert optimistic update
            setIssues(prev => prev.map(i => i.issueKey === issueKey
                ? { ...i, dayIndex: getIssueDayIndex({ startDate: oldStartDate, dueDate: oldDueDate }, weekDays), startDate: oldStartDate, dueDate: oldDueDate }
                : i));
        }
        finally {
            setMovingIssue(null);
        }
    }, [issues, weekDays, projectId]);
    const handleClose = useCallback(() => {
        if (hasMovedRef.current) {
            onIssuesMoved();
        }
        onClose();
    }, [onClose, onIssuesMoved]);
    // Group issues by day
    const issuesByDay = weekDays.map((_, dayIdx) => issues.filter(i => i.dayIndex === dayIdx));
    return (_jsx("div", { className: "fixed inset-0 z-[60] flex items-center justify-center bg-black/30", children: _jsxs("div", { ref: modalRef, className: "bg-white rounded-lg shadow-2xl w-[900px] max-w-[95vw] max-h-[80vh] flex flex-col", children: [_jsxs("div", { className: "flex items-center justify-between px-4 py-3 border-b border-gray-200", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("h3", { className: "text-sm font-semibold text-gray-800", children: formatWeekLabel(weekStartDate) }), _jsx("span", { className: "text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded", children: projectKey }), _jsxs("span", { className: "text-xs text-gray-500", children: [hours, "h"] })] }), _jsx("button", { onClick: handleClose, className: "p-1 text-gray-400 hover:text-gray-600 rounded", children: _jsx("svg", { className: "w-4 h-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) })] }), _jsx("div", { className: "flex-1 overflow-auto p-3", children: _jsx("div", { className: "grid grid-cols-5 gap-2 h-full min-h-[300px]", children: weekDays.map((day, dayIdx) => {
                            const { dayName, dayDate } = formatDayHeader(day);
                            const isToday = day === new Date().toISOString().slice(0, 10);
                            const isDragOver = dragOverDay === dayIdx;
                            const dayIssues = issuesByDay[dayIdx] || [];
                            return (_jsxs("div", { className: `flex flex-col rounded-lg border transition-colors ${isDragOver
                                    ? 'border-blue-400 bg-blue-50/50'
                                    : isToday
                                        ? 'border-blue-200 bg-blue-50/30'
                                        : 'border-gray-200 bg-gray-50/30'}`, onDragOver: e => handleDragOver(e, dayIdx), onDragLeave: handleDragLeave, onDrop: e => handleDrop(e, dayIdx), children: [_jsxs("div", { className: `px-2 py-1.5 border-b text-center ${isToday ? 'border-blue-200 bg-blue-100/50' : 'border-gray-100'}`, children: [_jsx("div", { className: `text-[10px] font-semibold uppercase tracking-wide ${isToday ? 'text-blue-700' : 'text-gray-500'}`, children: dayName }), _jsx("div", { className: `text-xs ${isToday ? 'text-blue-600 font-medium' : 'text-gray-400'}`, children: dayDate })] }), _jsxs("div", { className: "flex-1 p-1.5 space-y-1.5 overflow-y-auto min-h-[200px]", children: [dayIssues.length === 0 && (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsx("span", { className: "text-[10px] text-gray-300", children: "Drop here" }) })), dayIssues.map(issue => (_jsxs("div", { className: `p-2 bg-white rounded border shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${movingIssue === issue.issueKey
                                                    ? 'opacity-50 border-blue-300'
                                                    : 'border-gray-200'}`, draggable: true, onDragStart: e => handleDragStart(e, issue.issueKey), children: [_jsxs("div", { className: "flex items-center gap-1 mb-0.5", children: [jiraInstanceUrl ? (_jsx("a", { href: `${jiraInstanceUrl}/browse/${issue.issueKey}`, target: "_blank", rel: "noopener noreferrer", className: "text-[10px] font-medium text-primary-600 hover:underline flex-shrink-0", onClick: e => e.stopPropagation(), draggable: false, children: issue.issueKey })) : (_jsx("span", { className: "text-[10px] font-medium text-primary-600 flex-shrink-0", children: issue.issueKey })), _jsx("span", { className: "text-[9px] text-gray-400 truncate", children: issue.issueType })] }), _jsx("p", { className: "text-[10px] text-gray-700 leading-tight line-clamp-2 mb-1", children: issue.summary }), _jsxs("div", { className: "flex items-center gap-1 flex-wrap", children: [issue.status && (_jsx("span", { className: `inline-block px-1 py-0 rounded text-[8px] font-medium ${getStatusColor(issue.statusCategory || '')}`, children: issue.status })), _jsxs("span", { className: "text-[8px] text-gray-500 font-medium", children: [issue.storyPoints ?? '-', " SP"] }), issue.hoursLogged > 0 && (_jsxs("span", { className: "text-[8px] text-purple-600 font-medium", children: [issue.hoursLogged.toFixed(1), "h"] }))] })] }, issue.issueKey)))] }), _jsx("div", { className: "px-2 py-1 border-t border-gray-100 text-center", children: _jsxs("span", { className: "text-[9px] text-gray-400", children: [dayIssues.length, " ", dayIssues.length === 1 ? 'issue' : 'issues'] }) })] }, day));
                        }) }) }), _jsxs("div", { className: "flex items-center justify-between px-4 py-2.5 border-t border-gray-200 bg-gray-50 rounded-b-lg", children: [_jsx("span", { className: "text-xs text-gray-500", children: pendingChanges > 0
                                ? `${pendingChanges} date ${pendingChanges === 1 ? 'change' : 'changes'} pending approval`
                                : 'Drag issues between days to reschedule' }), _jsx("button", { onClick: handleClose, className: "px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50", children: "Close" })] })] }) }));
}
//# sourceMappingURL=WeekCalendarModal.js.map